import { Component } from 'react'
import PropTypes from 'prop-types'
import isEqual from 'lodash.isequal'
import groupBy from 'lodash.groupby'
import map from 'lodash.map'

import { makeCancelable, noop } from 'utils'

let images = {}
const imageCacheEl = document.querySelector('#image-cache')

const decodeImage = image => {
  return new Promise((resolve, reject) => {
    image.addEventListener('load', e => {
      if (window.Image.prototype.decode) {
        image.decode().then(resolve).catch(resolve)

        return
      }

      resolve(e)
    })

    image.addEventListener('error', e => {
      reject(e)
    })
  })
}

const loadImage = ({ src, srcSet }) => {
  const image = new window.Image()

  image.src = src
  if (srcSet) image.srcset = srcSet

  return decodeImage(image)
    .then(() => {
      images[image.src] = image
      if (imageCacheEl) imageCacheEl.appendChild(image)
    })
    .catch(noop)
}

function cleanUpCachedImages() {
  try {
    images = {}
    imageCacheEl.innerHTML = ''
  } catch (e) {
    // The error will be handled by the mutation "onError"
  }
}

class ImageFilePrefetcher extends Component {
  state = {
    totalRequests: 0,
    requestsSuccess: 0,
    requestsFailed: 0,
  }

  componentDidUpdate(previousProps) {
    if (isEqual(this.props.files, previousProps.files)) return

    this.preloadFiles(this.props)
  }

  componentDidMount() {
    this.preloadFiles(this.props)
  }

  onImageLoad = () => {
    this.setState(state => ({
      requestsSuccess: state.requestsSuccess + 1,
    }))
  }

  onImageError = () => {
    this.setState(state => ({
      requestsFailed: state.requestsFailed + 1,
    }))
  }

  preloadFiles = ({ files }) => {
    const getFileKey = ({ artboard, type }) => `${artboard}-${type}`
    const groupedByArtbordType = groupBy(files, getFileKey)
    const urlSortedByScale = files =>
      files.sort((a, b) => a.scale - b.scale).map(({ url }) => url)
    const fileUrls = map(groupedByArtbordType, urlSortedByScale)

    this.promises = fileUrls.map(urls =>
      makeCancelable(
        loadImage({
          src: urls[0],
          srcSet: urls.map((url, i) => `${url} ${i + 1}x`).join(', '),
        })
      )
    )

    this.setState({
      totalRequests: this.promises.length,
      requestsSuccess: 0,
      requestsFailed: 0,
    })

    this.promises.forEach(promise => {
      promise.then(this.onImageLoad).catch(this.onImageError)
    })
  }

  componentWillUnmount() {
    Array.isArray(this.promises) && this.promises.forEach(p => p.cancel())
    cleanUpCachedImages()
  }

  render() {
    if (this.state.totalRequests === 0) return this.props.children(0)

    return this.props.children(
      (this.state.requestsSuccess + this.state.requestsFailed) /
        this.state.totalRequests
    )
  }
}

ImageFilePrefetcher.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      identifier: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      scale: PropTypes.number.isRequired,
      artboard: PropTypes.string.isRequired,
    })
  ),
  children: PropTypes.func.isRequired,
}

export default ImageFilePrefetcher
